// Homepage Builder — section-type registry.
// 14 base templates shared by web (homepage_web_kickavenue) and app (homepage_app_kickavenue).
// `opts` is the per-template option matrix the inspector renders.
// Control kinds: cat, title, subtitle, action, source:<kind>, cardStyle, rows,
//   showRetail, image, bannerLayout, chips, overlay, icon, social, promoItems

export const SETTING_NAME = {
  web: 'homepage_web_kickavenue',
  app: 'homepage_app_kickavenue',
};

export const REGISTRY = {
  slider_banner: { label: 'Hero Banner', icon: 'fa-image', opts: ['cat', 'source:slider'] },
  shortcut: { label: 'Shortcut Grid', icon: 'fa-th', opts: ['cat', 'source:shortcut'] },
  brand_focus: { label: 'Brand Focus', icon: 'fa-star', opts: ['cat', 'title', 'subtitle', 'source:brand'] },
  // row1/row2 each have two visual variants (small_card = boxed card, no_bg_card = transparent),
  // toggled via the `cardStyle` control — so the label stays generic.
  row1_small_card: { label: 'Product Row 1', icon: 'fa-bars', opts: ['cat', 'title', 'subtitle', 'action', 'cardStyle', 'source:data', 'showRetail'] },
  row1_no_bg_card: { label: 'Product Row 1', icon: 'fa-bars', opts: ['cat', 'title', 'subtitle', 'action', 'cardStyle', 'source:data', 'showRetail'] },
  row2_small_card: { label: 'Product Row 2', icon: 'fa-th-list', opts: ['cat', 'title', 'subtitle', 'action', 'cardStyle', 'source:data', 'showRetail'] },
  row2_no_bg_card: { label: 'Product Row 2', icon: 'fa-th-list', opts: ['cat', 'title', 'subtitle', 'action', 'cardStyle', 'source:data', 'showRetail'] },
  row3: { label: 'Product Row 3', icon: 'fa-th', opts: ['cat', 'title', 'subtitle', 'action', 'source:data', 'showRetail'] },
  // banner_chip 1/2 rows toggled via the `rows` control — generic label.
  banner_chip_1_row: { label: 'Banner + Chips', icon: 'fa-id-card-o', opts: ['cat', 'title', 'subtitle', 'action', 'rows', 'bannerLayout', 'image', 'chips', 'overlay', 'source:data', 'showRetail'] },
  banner_chip_2_rows: { label: 'Banner + Chips', icon: 'fa-id-card', opts: ['cat', 'title', 'subtitle', 'action', 'rows', 'bannerLayout', 'image', 'chips', 'overlay', 'source:data', 'showRetail'] },
  // banner_only: just a clickable banner — no heading, chips, or product rows. Web publishes
  // data.banner_image(_landscape/_portrait); app publishes the single data.banner_image.
  banner_only: { label: 'Banner Only', icon: 'fa-image', opts: ['cat', 'image', 'source:data'] },
  break_with_bg: { label: 'Image Break', icon: 'fa-picture-o', opts: ['cat', 'title', 'subtitle', 'action', 'image', 'source:data', 'showRetail'] },
  break_without_bg: { label: 'Stats Break', icon: 'fa-bar-chart', opts: ['cat', 'title', 'subtitle', 'action', 'icon', 'social', 'source:data', 'showRetail'] },
  promo_banner: { label: 'Promo Banner', icon: 'fa-bullhorn', opts: ['cat', 'title', 'promoItems'] },
};

// Add-section picker: one card per distinct template (variants chosen via inspector toggles).
// { type: the default type string created, desc: one-line explanation }
export const TEMPLATES = [
  { type: 'slider_banner', desc: 'Full-width rotating banner carousel at the top.' },
  { type: 'shortcut', desc: 'Row of round category/shortcut icons.' },
  { type: 'brand_focus', desc: 'Horizontal strip of brand logos.' },
  { type: 'row1_no_bg_card', desc: 'One horizontal row of product cards.' },
  { type: 'row2_no_bg_card', desc: 'Two stacked rows of product cards.' },
  { type: 'row3', desc: 'Three stacked rows of product cards.' },
  { type: 'banner_chip_2_rows', desc: 'Campaign banner + filter chips + product rows.' },
  { type: 'banner_only', desc: 'Single clickable banner — no chips or product rows.' },
  { type: 'break_with_bg', desc: 'Full-image promo break that links to a collection.' },
  { type: 'break_without_bg', desc: 'Icon + headline + social-proof stats (e.g. 10K+ shipped).' },
  // promo_banner is intentionally NOT offered here: its per-slide editor isn't built
  // yet, so a freshly added promo section can't be made valid. It stays in REGISTRY so
  // existing promo sections still render and round-trip. Re-add once the slide editor lands.
];

// homepage_category values present in the real rows (app uses tabs; web is allCategory only).
export const CATS = [
  ['allCategory', 'All'],
  ['sneakers', 'Sneakers'],
  ['apparels', 'Apparels'],
  ['handbags', 'Handbags'],
  ['lifestyles', 'Lifestyle'],
];

// Curated product "rails" (verified against be-api-laravel ProductController/routes).
//   cat: category-scoped (false = user/IP-global, ignores category, e.g. last-seen)
//   range: honors range_type/range_value (a sales/activity window)
// A `web: false` flag hides a rail in the web builder (see SourceModal) — used when the
// Next RANKED_VARIANT_ENDPOINTS allowlist lacks it. All current rails are in the allowlist.
export const RAILS = [
  { label: 'Top Selling', path: '/products/top-selling', cat: true, range: true },
  { label: 'Trending', path: '/products/trends', cat: true, range: true },
  { label: 'Market Update', path: '/products/market-update', cat: true, range: false },
  { label: 'Price Drop', path: '/products/price-drop', cat: true, range: false },
  { label: 'Upcoming Releases', path: '/products/unreleased', cat: true, range: false },
  { label: "Editors' Choice", path: '/products/editors-choices', cat: true, range: false },
  { label: 'New Highest Offer', path: '/products/highest-bid', cat: true, range: true },
  { label: 'Recently Viewed', path: '/products/last-seen', cat: false, range: false },
];

// Build a rail endpoint with the chosen category + defaults. 'all_category' uses the BE's
// cross-category mix (category=all_category&homepage_all_category=TRUE). Exceptions: highest-bid
// filters by category NAME (no mix) so "all" omits category there; last-seen ignores category.
export function railEndpoint(rail, category) {
  const all = !category || category === 'all_category';
  const params = [];
  if (rail.path !== '/products/last-seen') params.push('availables=TRUE');
  if (rail.cat) {
    if (!all) {
      params.push(`category=${category}`);
    } else if (rail.path !== '/products/highest-bid') {
      params.push('category=all_category');
      params.push('homepage_all_category=TRUE');
    }
  }
  if (rail.range) { params.push('range_type=week'); params.push('range_value=1'); }
  params.push('per_page=20');
  return `${rail.path}?${params.join('&')}`;
}

export const ENDPOINT_PREFIXES = ['/search', '/products/', '/collections/', '/brands/', '/sliders', '/settings?name='];

// Endpoint types the product-list renderers (rows, banner-chip, break) actually support.
// The live web renderers hide a section whose endpoint_type isn't one of these, so a
// brand/slider/shortcut source on a product-style section would publish an empty section.
export const PRODUCT_SOURCE_TYPES = ['search', 'products', 'collection'];

// Product-style template family: these share title/subtitle/action/content-source/showRetail,
// so an existing section can switch base template in place without losing its source.
// Non-family types (slider/shortcut/brand/promo/unknown) stay fixed — their sources are
// incompatible, and unknown types must round-trip verbatim (see toWire).
export const TYPE_FAMILY = [
  ['row1', 'Product Row 1'],
  ['row2', 'Product Row 2'],
  ['row3', 'Product Row 3'],
  ['banner_chip', 'Banner + Chips'],
  ['break_with_bg', 'Image Break'],
  ['break_without_bg', 'Stats Break'],
];

export function familyKey(type) {
  // Prefix matching alone would also claim an unknown forward-compat type (a future
  // `row1_glass_card`), hiding its real name behind a family label and offering a switch that
  // discards the verbatim `_raw` round-trip toWire gives unknown types. Every family template
  // is a REGISTRY key, so gating on that costs nothing and keeps unknown types read-only.
  if (!REGISTRY[type]) return null;
  if (type === 'row3' || type === 'break_with_bg' || type === 'break_without_bg') return type;
  if (type.indexOf('row1_') === 0) return 'row1';
  if (type.indexOf('row2_') === 0) return 'row2';
  if (type.indexOf('banner_chip') === 0) return 'banner_chip';
  return null;
}

// Patch that switches a section to another family template: the new concrete type plus
// resets of editor fields the target doesn't render — otherwise stale state (chips from a
// former banner_chip life) would ship in the wire payload via toWire's shared emitters.
// Variant traits carry over where meaningful: row1↔row2 keep the card style; entering
// banner_chip/row defaults match the add-picker (2 rows / no_bg_card).
export function switchTypePatch(s, targetKey) {
  const cardStyle = s.type.indexOf('_small_card') !== -1 ? '_small_card' : '_no_bg_card';
  let type = targetKey;
  if (targetKey === 'row1' || targetKey === 'row2') type = targetKey + cardStyle;
  else if (targetKey === 'banner_chip') type = s.type.indexOf('banner_chip') === 0 ? s.type : 'banner_chip_2_rows';
  const patch = { type };
  // Entering banner_chip from outside the family starts on the add-picker's default layout;
  // bannerLayout is editor-only state that survives a trip through another template, so a
  // former 'chips'/'banner' choice would otherwise preselect a pill the user never picked.
  if (targetKey === 'banner_chip' && s.type.indexOf('banner_chip') !== 0) patch.bannerLayout = 'banner_chips';
  if (targetKey !== 'banner_chip') { patch.chips = []; patch.overlay = {}; }
  // The image survives between the two image-bearing templates (banner ↔ background).
  if (targetKey !== 'banner_chip' && targetKey !== 'break_with_bg') { patch.bannerImg = ''; patch.bannerImgMobile = ''; }
  if (targetKey !== 'break_without_bg') { patch.icon = ''; patch.social = []; }
  return patch;
}

// The authored content a switch patch would discard, described for a confirm prompt. Derived
// from the patch itself so it can't drift from switchTypePatch. Empty when the switch is
// lossless — the common row1 ↔ row2 ↔ row3 case stays a single click.
export function switchTypeLoss(s, patch) {
  const out = [];
  const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;
  if ('chips' in patch) {
    const n = (s.chips || []).filter((c) => c.name).length;
    if (n) out.push(plural(n, 'chip'));
  }
  if ('overlay' in patch) {
    const o = s.overlay || {};
    if (o.badge || o.title || o.subtitle) out.push('the banner overlay');
  }
  if ('bannerImg' in patch && (s.bannerImg || s.bannerImgMobile)) out.push('the image');
  if ('icon' in patch && s.icon) out.push('the icon');
  if ('social' in patch) {
    const n = (s.social || []).filter((m) => m.number && m.text).length;
    if (n) out.push(plural(n, 'stat'));
  }
  return out;
}

let _idCounter = 0;
const isBreak = (type) => type === 'break_with_bg' || type === 'break_without_bg';

// Wire field contract, verified against BOTH renderers (Flutter parser + web components):
// - banner_chip image: data.banner_image (Flutter reads only this); web also reads _landscape/_portrait.
// - break image: top-level `image` (+ _landscape/_portrait for web). NOT under data.
// - chips: data.chips[] = {name, endpoint_type, endpoint, slug}.
// - banner_overlay: data.banner_overlay {badge,title,subtitle} — WEB ONLY.
// - icon: top-level URL.  social_proof: top-level [{number,text}].  promo: data.items[] = {image,endpoint_type,endpoint,slug}.
// - rows endpoint/type/slug: under data{}.
// Rule: emit the single key (banner_image/image/item.image); add _landscape/_portrait only as web extras.

// Builder-only stable React keys for repeatable rows (chips, social); stripped by stripK().
export function newRowKey() { return `row_${++_idCounter}`; }
function stripK(o) { const c = Object.assign({}, o); delete c._k; return c; }

// Percent-encode the chars that would break an inline CSS url('...') token (quotes, parens,
// backslash, whitespace) so an admin-supplied image URL containing one can't escape the style.
// Encode by char code, NOT encodeURIComponent — the latter leaves ' ( ) unescaped (the exact
// chars that close the url('...') token).
export function cssUrl(u) {
  return String(u || '').replace(/["'()\\\s]/g, (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0'));
}

// Mirror the live web resolveShowUnderRetail: a MISSING/null value defaults to ON (true);
// only an explicit false-y value turns it off. Used so the CMS toggle reflects production.
function resolveShowUnderRetail(raw) {
  if (raw === undefined || raw === null) return true;
  if (typeof raw === 'string') return raw !== 'false' && raw !== '0';
  if (typeof raw === 'number') return raw !== 0;
  return Boolean(raw);
}

export function fromWire(item) {
  const data = item.data || {};
  const chips = (data.chips || []).map((c) => Object.assign({ _k: newRowKey() }, typeof c === 'string' ? { name: c } : c));
  // Two slots, each falling back through the storefront's own render chain so the loaded state
  // matches what ships and legacy single-crop rows load valid (not blocked by the web mobile gate):
  // desktop = landscape || universal; mobile = portrait || landscape || universal. Portrait-first
  // still preserves a distinct mobile crop — the fallback only fills mobile when portrait is absent.
  const bannerImg = isBreak(item.type)
    ? (item.image_landscape || item.image || '')
    : (data.banner_image_landscape || data.banner_image || '');
  const bannerImgMobile = isBreak(item.type)
    ? (item.image_portrait || item.image_landscape || item.image || '')
    : (data.banner_image_portrait || data.banner_image_landscape || data.banner_image || '');
  return {
    _id: `hpb_${++_idCounter}`, // stable React key for the reorderable list
    type: item.type,
    title: item.title || '',
    subtitle: item.subtitle || '',
    action_title: item.action_title || '',
    homepage_category: item.homepage_category || 'allCategory',
    show_under_retail: resolveShowUnderRetail(item.show_under_retail),
    icon: item.icon || '', // image URL
    social: (item.social_proof || []).map((m) => Object.assign({ _k: newRowKey() }, m)),
    endpoint: item.endpoint != null ? item.endpoint : data.endpoint || '',
    endpoint_type: item.endpoint_type != null ? item.endpoint_type : data.endpoint_type || '',
    slug: item.slug != null ? item.slug : data.slug || '',
    source: deriveSourceLabel(item.endpoint_type || data.endpoint_type, data.slug || item.slug, data.endpoint || item.endpoint),
    bannerImg,
    bannerImgMobile,
    chips,
    overlay: Object.assign({}, data.banner_overlay || {}),
    promoItems: (data.items || []).map((it) => Object.assign({}, it)),
    bannerLayout: item.bannerLayout || (bannerImg && !chips.length ? 'banner' : (!bannerImg && chips.length ? 'chips' : 'banner_chips')),
    _raw: item, // preserve unknown keys for lossless round-trip
  };
}

export function toWire(s, platform) {
  // Unknown/forward-compat types (not in REGISTRY) have no inspector and no managed field
  // contract — round-trip the loaded wire object verbatim instead of running the known-type
  // normalizers below, which delete top-level endpoint/image/etc. and would silently corrupt
  // a future section shape. validateSection() likewise doesn't block unknown types.
  if (!REGISTRY[s.type]) return Object.assign({}, s._raw || { type: s.type });
  const out = Object.assign({}, s._raw || {});
  out.type = s.type;
  const isApp = platform === 'app'; // app reads only the universal banner_image/image — skip the web-only orientation crops
  out.homepage_category = s.homepage_category || 'allCategory';
  // banner_only has no heading fields in its inspector — never publish title/subtitle/action
  // (an uneditable leftover would render as a heading the admin can't remove).
  const isBannerOnly = s.type === 'banner_only';
  if (isBannerOnly) {
    delete out.title; delete out.subtitle; delete out.action_title;
  } else {
    setOrDelete(out, 'title', s.title);
    setOrDelete(out, 'subtitle', s.subtitle);
    setOrDelete(out, 'action_title', s.action_title);
  }
  // Persist an explicit OFF as `false`; omit when ON (the live renderer defaults missing
  // → true). Deleting the key on false would lose the OFF state and render ON in prod.
  // banner_only has no product rows, so the flag is meaningless there — never emit it.
  if (s.show_under_retail || isBannerOnly) delete out.show_under_retail; else out.show_under_retail = false;
  // icon is uneditable for banner_only (not in its opts) — strip it like title/overlay/chips.
  setOrDelete(out, 'icon', isBannerOnly ? '' : s.icon);
  // social_proof belongs ONLY to break_without_bg, and the live renderer counts only
  // metrics with both number AND text. break_with_bg renders null if social_proof exists
  // at all, so never emit it for other types (prevents a publish-then-disappear edge).
  const social = s.type === 'break_without_bg'
    ? (s.social || []).filter((m) => m.number && m.text).map(stripK)
    : [];
  if (social.length) out.social_proof = social; else delete out.social_proof;
  // editor lifts endpoint/type/slug to top level — clear them, real home is data{}
  delete out.endpoint; delete out.endpoint_type; delete out.slug;

  // top-level break image: universal `image`/`image_landscape` = desktop (Flutter reads `image`);
  // `image_portrait` = the mobile crop (web reads it on mobile, falls back to landscape if absent).
  if (isBreak(s.type)) {
    setOrDelete(out, 'image', s.bannerImg);
    if (isApp) { delete out.image_landscape; delete out.image_portrait; }
    else { setOrDelete(out, 'image_landscape', s.bannerImg); setOrDelete(out, 'image_portrait', s.bannerImgMobile); }
  } else {
    delete out.image; delete out.image_landscape; delete out.image_portrait;
  }

  const data = Object.assign({}, out.data || {});
  setOrDelete(data, 'endpoint', s.endpoint);
  setOrDelete(data, 'endpoint_type', s.endpoint_type);
  setOrDelete(data, 'slug', s.slug);

  // Honor bannerLayout (banner_chip types only): renderers infer banner-only/chips-only
  // from which fields are present, so 'banner' must omit chips and 'chips' must omit the
  // banner image — even if leftover state lingers (else "Banner only" still ships chips).
  const isBannerChip = s.type.indexOf('banner_chip') === 0;
  const lay = isBannerChip ? (s.bannerLayout || 'banner_chips') : null;
  const wantBanner = lay !== 'chips'; // banner kept unless chips-only
  // banner_only must never ship chips (its contract is banner + source only) — force them
  // off even if a hand-edited wire row carried chips into _raw.
  const wantChips = lay !== 'banner' && !isBannerOnly; // chips kept unless banner-only

  if (!isBreak(s.type) && s.bannerImg && wantBanner) {
    data.banner_image = s.bannerImg; // universal — Flutter + web fallback
    if (isApp) {
      delete data.banner_image_landscape; delete data.banner_image_portrait;
    } else {
      data.banner_image_landscape = s.bannerImg;
      // web mobile crop (validateSection requires it on web; renderer falls back to landscape if absent)
      setOrDelete(data, 'banner_image_portrait', s.bannerImgMobile);
    }
  } else {
    delete data.banner_image; delete data.banner_image_landscape; delete data.banner_image_portrait;
  }

  const chips = (s.chips || []).filter((c) => c.name).map(stripK);
  if (chips.length && wantChips) data.chips = chips; else delete data.chips;

  if (!isBannerOnly && s.overlay && (s.overlay.badge || s.overlay.title || s.overlay.subtitle)) data.banner_overlay = s.overlay; else delete data.banner_overlay;

  const items = (s.promoItems || []).filter((it) => it.image || it.endpoint);
  if (items.length && !isBannerOnly) data.items = items; else delete data.items;

  if (Object.keys(data).length) out.data = data; else delete out.data;
  return out;
}

// Per-section required-field validation (rules from the verified render matrix).
// Returns { valid, reason }. Used for the per-section badge + Save gate.
// `platform` gates the mobile-image requirement: orientation crops are a WEB concept (web has
// desktop+mobile viewports). The Flutter app is mobile-only and reads a single `banner_image`,
// so app sections require only the one image — requiring a separate mobile crop there would block
// existing single-image app rows that the app renders fine.
export function validateSection(s, platform) {
  // fromWire backfills mobile for loaded rows, so these web gates only bite a freshly-added desktop-only section.
  const needMobileImg = platform === 'web';
  const t = s.type;
  const ok = { valid: true, reason: '' };
  const bad = (reason) => ({ valid: false, reason });
  // Live BreakSection counts only metrics with BOTH number AND text (isValidMetric) and
  // hides the section below 3 — match that so half-filled rows don't pass Save then render null.
  const hasSocial = (s.social || []).filter((m) => m.number && m.text).length;
  // A product-style section needs a source type the renderer can show (search/products/
  // collection). The live row/break/banner-chip renderers return null when endpoint_type
  // is missing OR unsupported, so both cases must fail validation (not just a wrong type).
  const badSrcType = (et) => {
    if (!et) return 'content source is missing its type — re-pick the source';
    if (!PRODUCT_SOURCE_TYPES.includes(et)) return `a ${et} source can't render here — use a collection, search, or product list`;
    return null;
  };
  if (t === 'slider_banner' || t === 'shortcut') return s.endpoint ? ok : bad('needs a content source');
  if (t === 'banner_only') {
    // The source-type restriction is per the KB-5373 contract: banner_only's endpoint stays
    // aligned with the product-style direction (search/collection/products), same as rows.
    if (!s.bannerImg) return bad('needs a banner image');
    if (needMobileImg && !s.bannerImgMobile) return bad('needs a portrait (mobile) banner image');
    if (!s.endpoint) return bad('needs a content source');
    const bt = badSrcType(s.endpoint_type);
    if (bt) return bad(bt);
    return ok;
  }
  // Live BrandFocusSection returns null unless data.endpoint_type === 'brand', so require
  // BOTH a brand type and an endpoint (a non-brand endpoint would publish an empty section).
  if (t === 'brand_focus') return s.endpoint_type === 'brand' && s.endpoint ? ok : bad('needs a brand source');
  if (t.indexOf('row') === 0) {
    if (!s.endpoint) return bad('needs a content source');
    const bt = badSrcType(s.endpoint_type);
    if (bt) return bad(bt);
    if (!s.title) return bad('needs a title');
    return ok;
  }
  if (t.indexOf('banner_chip') === 0) {
    const lay = s.bannerLayout || 'banner_chips';
    const chipsList = (s.chips || []).filter((c) => c.name);
    if (lay !== 'chips' && !s.bannerImg) return bad('needs a banner image');
    if (lay !== 'chips' && needMobileImg && !s.bannerImgMobile) return bad('needs a mobile (portrait) banner image');
    if (lay === 'chips' && !chipsList.length) return bad('needs at least one chip');
    // Chips are shown unless the layout is banner-only. Each shown chip filters the
    // product list via its own endpoint, so a label-only chip would render nothing.
    if (lay !== 'banner') {
      const noSrc = chipsList.find((c) => !c.endpoint);
      if (noSrc) return bad(`chip “${noSrc.name}” needs a content source (use the filter button)`);
      const badChip = chipsList.find((c) => badSrcType(c.endpoint_type));
      if (badChip) return bad(`chip “${badChip.name}” — ${badSrcType(badChip.endpoint_type)}`);
    }
    if (!s.endpoint) return bad('needs a content source');
    const bt = badSrcType(s.endpoint_type);
    if (bt) return bad(bt);
    return ok;
  }
  if (t === 'break_with_bg') {
    if (!s.bannerImg) return bad('needs a background image');
    if (needMobileImg && !s.bannerImgMobile) return bad('needs a mobile (portrait) background image');
    if (!s.endpoint) return bad('needs a content source');
    const bt = badSrcType(s.endpoint_type);
    if (bt) return bad(bt);
    return ok;
  }
  if (t === 'break_without_bg') {
    if (!s.icon) return bad('needs an icon');
    if (hasSocial < 3) return bad('needs at least 3 social-proof metrics');
    if (!s.endpoint) return bad('needs a content source');
    const bt = badSrcType(s.endpoint_type);
    if (bt) return bad(bt);
    return ok;
  }
  if (t === 'promo_banner') return (s.promoItems || []).length ? ok : bad('needs at least one promo slide');
  return ok; // unknown/forward-compat types: don't block
}

function deriveSourceLabel(et, slug, endpoint) {
  if (!et && !endpoint) return '';
  if (et === 'collection' && slug) return `Collection · ${slug}`;
  if (et === 'brand') return 'Popular brands';
  if (et === 'slider') return 'Slider set';
  if (et === 'products' && endpoint) return endpoint.split('?')[0];
  if (et === 'search') return 'Search filter';
  return endpoint ? endpoint.split('?')[0] : (et || '');
}

function setOrDelete(obj, key, val) {
  if (val !== undefined && val !== null && val !== '') obj[key] = val;
  else delete obj[key];
}
